import { useEffect, useRef, useState } from "react"
import { N_PETALI, VALORE_MIN, decimaleVoto, etichettaVoto } from "../api/tipi"
import type { ValueHalf } from "../api/tipi"
import "./corolla.css"

const CX = 180
const CY = 205
const R_EST = 152
const R_INT = 86
const ARCO = 164 // gradi coperti dai 19 petali
const PASSO = ARCO / (N_PETALI - 1)

/** Quanti scaglioni di ritardo esistono in CSS: oltre, si sbocca insieme.
 *  Il gruppo non arriverà mai a dodici giurati, ma se succedesse la
 *  fioritura resterebbe corretta, solo meno scaglionata. */
const RITARDI = 12

/** Quando si vota da tastiera si conferma dopo una pausa, invece che a ogni
 *  freccia: l'upsert è idempotente ma non serve inondarlo di richieste. */
const PAUSA_TASTIERA_MS = 500

const gradi = (indice: number) => -ARCO / 2 + indice * PASSO

export interface PetaloAltrui {
  nome: string
  valueHalf: ValueHalf
}

interface Props {
  valore: ValueHalf | null
  nc: boolean
  /** Nome della custom property del colore: `--esibizione`, `--look`, … */
  famiglia: string
  etichetta: string
  disabilitato?: boolean
  onConferma: (valore: ValueHalf) => void
  /** Voti degli altri, presenti solo dopo il reveal. */
  fioritura?: PetaloAltrui[] | null
  media?: number | null
}

export function Corolla({
  valore,
  nc,
  famiglia,
  etichetta,
  disabilitato = false,
  onConferma,
  fioritura = null,
  media = null,
}: Props) {
  const svg = useRef<SVGSVGElement>(null)
  const [anteprima, setAnteprima] = useState<number | null>(null)
  const timerTastiera = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(timerTastiera.current), [])

  const indiceScelto = valore != null ? valore - VALORE_MIN : null
  const indiceMostrato = anteprima ?? indiceScelto
  const inFioritura = fioritura != null && fioritura.length > 0
  const colore = `var(${famiglia})`

  /** Solo l'angolo conta: sbagliare distanza dal centro non costa nulla. */
  function indiceDaPuntatore(ev: React.PointerEvent): number {
    const riquadro = svg.current!.getBoundingClientRect()
    const x = ((ev.clientX - riquadro.left) / riquadro.width) * 360 - CX
    const y = ((ev.clientY - riquadro.top) / riquadro.height) * 230 - CY
    const angolo = (Math.atan2(x, -y) * 180) / Math.PI
    return limita(Math.round((angolo + ARCO / 2) / PASSO))
  }

  function limita(i: number): number {
    return Math.max(0, Math.min(N_PETALI - 1, i))
  }

  function trascina(ev: React.PointerEvent) {
    const i = indiceDaPuntatore(ev)
    if (i !== anteprima) {
      setAnteprima(i)
      navigator.vibrate?.(4)
    }
  }

  function iniziaTrascinamento(ev: React.PointerEvent) {
    if (disabilitato) return
    svg.current?.setPointerCapture(ev.pointerId)
    trascina(ev)
  }

  /** Conferma al rilascio: fino a lì si può ancora correggere, ed è questo
   *  che rende accettabili petali più stretti di 44px (DESIGN.md §7). */
  function rilascia(ev: React.PointerEvent) {
    if (disabilitato || anteprima == null) return
    svg.current?.releasePointerCapture(ev.pointerId)
    onConferma(anteprima + VALORE_MIN)
    setAnteprima(null)
  }

  function daTastiera(ev: React.KeyboardEvent) {
    const verso = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1 }[ev.key]
    if (!verso || disabilitato) return
    ev.preventDefault()
    const partenza = indiceMostrato ?? Math.floor(N_PETALI / 2)
    const prossimo = limita(partenza + verso)
    setAnteprima(prossimo)
    clearTimeout(timerTastiera.current)
    timerTastiera.current = setTimeout(() => {
      onConferma(prossimo + VALORE_MIN)
      setAnteprima(null)
    }, PAUSA_TASTIERA_MS)
  }

  return (
    <svg
      ref={svg}
      className={`corolla ${inFioritura ? "corolla--fioritura" : ""}`}
      viewBox="0 0 360 230"
      role="slider"
      tabIndex={disabilitato ? -1 : 0}
      aria-label={`Voto ${etichetta.toLowerCase()}`}
      aria-disabled={disabilitato}
      aria-valuemin={1}
      aria-valuemax={10}
      aria-valuenow={valore != null ? valore / 2 : undefined}
      aria-valuetext={nc ? "NC, non giudico" : valore != null ? etichettaVoto(valore) : "nessun voto"}
      onPointerDown={iniziaTrascinamento}
      onPointerMove={(ev) => svg.current?.hasPointerCapture(ev.pointerId) && trascina(ev)}
      onPointerUp={rilascia}
      onPointerCancel={() => setAnteprima(null)}
      onKeyDown={daTastiera}
    >
      {Array.from({ length: N_PETALI }, (_, i) => {
        const mio = i === indiceMostrato && !nc
        const larghezza = mio ? 17 : 12
        const sporgenza = mio ? 8 : 0
        return (
          <g key={i} transform={`rotate(${gradi(i)} ${CX} ${CY})`}>
            <rect
              className="corolla__petalo"
              x={CX - larghezza / 2}
              y={CY - R_EST - sporgenza}
              width={larghezza}
              height={R_EST - R_INT + sporgenza}
              rx={larghezza / 2}
              fill={mio ? colore : "var(--petalo-spento)"}
            />
          </g>
        )
      })}

      {inFioritura && <Fioritura petali={fioritura} mioIndice={indiceScelto} />}

      <Cuore
        nc={nc}
        colore={colore}
        etichetta={
          inFioritura && media != null
            ? "Media"
            : // Senza un voto la corolla non dice cosa farne: qui l'etichetta
              // smette di nominare la categoria e spiega il gesto.
              !nc && indiceMostrato == null && !disabilitato
              ? "Trascina sull'arco"
              : etichetta
        }
        testo={
          inFioritura && media != null
            ? decimaleVoto(media * 2)
            : nc
              ? "NC"
              : indiceMostrato != null
                ? etichettaVoto(indiceMostrato + VALORE_MIN)
                : "—"
        }
        spento={!nc && indiceMostrato == null && !inFioritura}
        evidenza={inFioritura && media != null}
      />
    </svg>
  )
}

function Cuore({
  testo,
  etichetta,
  colore,
  nc,
  spento,
  evidenza,
}: {
  testo: string
  etichetta: string
  colore: string
  nc: boolean
  spento: boolean
  evidenza: boolean
}) {
  const tinta = evidenza ? "var(--evidenza)" : nc || spento ? "var(--inchiostro-lieve)" : colore
  return (
    <>
      <text
        className="corolla__valore"
        x={CX}
        y={CY - 16}
        textAnchor="middle"
        fontSize={testo.length > 3 ? 44 : 62}
        fill={tinta}
      >
        {testo}
      </text>
      <text className="corolla__etichetta" x={CX} y={CY + 12} textAnchor="middle" fill="var(--inchiostro-lieve)">
        {etichetta}
      </text>
    </>
  )
}

/** I petali degli altri.
 *
 *  I nomi non stanno sull'arco: ruotati agli estremi finivano fuori dal
 *  riquadro e con otto giurati si accavallavano comunque. Qui resta il
 *  colpo d'occhio — accordo o disaccordo si leggono dalla forma — e i nomi
 *  vanno nella legenda sotto la corolla.
 */
function Fioritura({ petali, mioIndice }: { petali: PetaloAltrui[]; mioIndice: number | null }) {
  const ordinati = [...petali].sort((a, b) => a.valueHalf - b.valueHalf)
  return (
    <>
      {ordinati.map((p, k) => {
        const i = p.valueHalf - VALORE_MIN
        return (
          <g key={`${p.nome}-${k}`} transform={`rotate(${gradi(i)} ${CX} ${CY})`}>
            <rect
              // Il ritardo arriva da una classe e non da `style={{…}}`: senza
              // stili inline la Content-Security-Policy può restare
              // `style-src 'self'`, senza 'unsafe-inline'.
              className={`corolla__sboccia corolla__sboccia--${Math.min(k, RITARDI - 1)}`}
              x={CX - 8.5}
              y={CY - R_EST - 8}
              width={17}
              height={R_EST - R_INT + 8}
              rx={8.5}
              fill={i === mioIndice ? "var(--struttura)" : "var(--evidenza)"}
            />
          </g>
        )
      })}
    </>
  )
}
